import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, Percent, DollarSign } from "lucide-react";
import { toast } from "sonner";

interface CommissionTier {
  id: string;
  min_amount: number;
  max_amount: number | null;
  commission_rate: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AdminCommissionSettings = () => {
  const [tiers, setTiers] = useState<CommissionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<CommissionTier | null>(null);

  // Form state
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [commissionRate, setCommissionRate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const { data, error } = await supabase
        .from("commission_tiers")
        .select("*")
        .order("min_amount", { ascending: true });

      if (error) {
        console.error("Error fetching tiers:", error);
        toast.error("فشل في جلب بيانات العمولات");
        return;
      }

      setTiers(data || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMinAmount("");
    setMaxAmount("");
    setCommissionRate("");
    setIsActive(true);
    setEditingTier(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (tier: CommissionTier) => {
    setEditingTier(tier);
    setMinAmount(tier.min_amount.toString());
    setMaxAmount(tier.max_amount?.toString() || "");
    setCommissionRate(tier.commission_rate.toString());
    setIsActive(tier.is_active);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Validation
    const min = parseFloat(minAmount);
    const max = maxAmount ? parseFloat(maxAmount) : null;
    const rate = parseFloat(commissionRate);

    if (isNaN(min) || min < 0) {
      toast.error("الحد الأدنى يجب أن يكون رقماً صحيحاً");
      return;
    }

    if (maxAmount && (isNaN(max!) || max! <= min)) {
      toast.error("الحد الأقصى يجب أن يكون أكبر من الحد الأدنى");
      return;
    }

    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("نسبة العمولة يجب أن تكون بين 0 و 100");
      return;
    }

    setSaving(true);

    try {
      if (editingTier) {
        // Update existing tier
        const { error } = await supabase
          .from("commission_tiers")
          .update({
            min_amount: min,
            max_amount: max,
            commission_rate: rate,
            is_active: isActive,
          })
          .eq("id", editingTier.id);

        if (error) throw error;
        toast.success("تم تحديث شريحة العمولة بنجاح");
      } else {
        // Insert new tier
        const { error } = await supabase.from("commission_tiers").insert({
          min_amount: min,
          max_amount: max,
          commission_rate: rate,
          is_active: isActive,
        });

        if (error) throw error;
        toast.success("تمت إضافة شريحة العمولة بنجاح");
      }

      setDialogOpen(false);
      resetForm();
      fetchTiers();
    } catch (error: any) {
      console.error("Error saving tier:", error);
      toast.error(error.message || "فشل في حفظ البيانات");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId: string) => {
    try {
      const { error } = await supabase
        .from("commission_tiers")
        .delete()
        .eq("id", tierId);

      if (error) throw error;
      toast.success("تم حذف شريحة العمولة");
      fetchTiers();
    } catch (error: any) {
      console.error("Error deleting tier:", error);
      toast.error("فشل في حذف الشريحة");
    }
  };

  const handleToggleActive = async (tier: CommissionTier) => {
    try {
      const { error } = await supabase
        .from("commission_tiers")
        .update({ is_active: !tier.is_active })
        .eq("id", tier.id);

      if (error) throw error;
      toast.success(tier.is_active ? "تم تعطيل الشريحة" : "تم تفعيل الشريحة");
      fetchTiers();
    } catch (error: any) {
      console.error("Error toggling tier:", error);
      toast.error("فشل في تغيير الحالة");
    }
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null) return "∞";
    return `$${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="glass border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-foreground flex items-center gap-2">
          <Percent className="w-5 h-5" />
          إعدادات العمولة
        </CardTitle>
        <Button onClick={openAddDialog} className="gap-2">
          <Plus className="w-4 h-4" />
          إضافة شريحة
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-4 bg-secondary/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            يتم تطبيق نسبة العمولة تلقائياً على المعاملات حسب المبلغ. إذا لم يتطابق المبلغ مع أي شريحة، يتم استخدام النسبة الافتراضية (20%).
          </p>
        </div>

        {tiers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            لا توجد شرائح عمولة. أضف شريحة جديدة للبدء.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-right text-muted-foreground">من (USD)</TableHead>
                  <TableHead className="text-right text-muted-foreground">إلى (USD)</TableHead>
                  <TableHead className="text-right text-muted-foreground">نسبة العمولة</TableHead>
                  <TableHead className="text-right text-muted-foreground">الحالة</TableHead>
                  <TableHead className="text-right text-muted-foreground">الإجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((tier) => (
                  <TableRow key={tier.id} className="border-border">
                    <TableCell className="text-foreground font-mono">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        {tier.min_amount.toLocaleString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-foreground font-mono">
                      {formatAmount(tier.max_amount)}
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-primary text-lg">
                        {tier.commission_rate}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={tier.is_active}
                        onCheckedChange={() => handleToggleActive(tier)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(tier)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive/80"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent dir="rtl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>حذف شريحة العمولة؟</AlertDialogTitle>
                              <AlertDialogDescription>
                                هل أنت متأكد من حذف هذه الشريحة؟ لا يمكن التراجع عن هذا الإجراء.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex-row-reverse gap-2">
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(tier.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent dir="rtl" className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingTier ? "تعديل شريحة العمولة" : "إضافة شريحة عمولة جديدة"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>من (USD)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>إلى (USD)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="بلا حدود"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">اتركه فارغاً لبلا حدود</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>نسبة العمولة (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="20"
                    value={commissionRate}
                    onChange={(e) => setCommissionRate(e.target.value)}
                    className="pl-8"
                  />
                  <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label>مفعّلة</Label>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>
            </div>

            <DialogFooter className="flex-row-reverse gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin ml-2" />
                    جاري الحفظ...
                  </>
                ) : (
                  "حفظ"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AdminCommissionSettings;
